import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Loader2,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import SearchableSelect, { SearchableOption } from './SearchableSelect';
import {
  getAllConfigs,
  getConfigStats,
  getConfigurableCompanies,
  getConfigurableContext,
  getConnectionConfigs,
  getConnectionTables,
  getDataConnections,
  revokeDataServiceConfig,
  saveDataServiceConfig,
  updateDataServiceConfig,
} from '../../Services/DataService';
import {
  CONFIG_SCOPE_OPTIONS,
  ConfigurableCompany,
  ConfigurableContext,
  DataConnection,
  DataServiceConfig,
  DataServiceConfigScope,
  DataServiceConfigSource,
  DataServiceConfigStats,
  TABLE_ACCESS_MODE_OPTIONS,
  TableAccessMode,
} from '../../Types/DataServiceType';

/** `datetime-local` wants a local `YYYY-MM-DDTHH:mm`, not an ISO instant. */
const toLocalInput = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

/**
 * Team Access is two lists of the same kind of record, so the split is a framing
 * choice rather than a different model: the tab decides who decided the grant.
 */
const ACCESS_TABS: { value: DataServiceConfigSource; label: string; hint: string }[] = [
  {
    value: 'MANUAL',
    label: 'Manual Access',
    hint: 'Granted by an admin, company by company or person by person.',
  },
  {
    value: 'SUBSCRIPTION',
    label: 'Payment / Subscription Automation',
    hint: 'Granted by a verified payment for a Data Service plan.',
  },
];

/**
 * Per-company and per-user table configuration.
 *
 * <p>Two halves that are deliberately independent. The form grants access; the
 * table below it lists everything the caller may see, searched and filterable.</p>
 *
 * <p>They used to be coupled: the table listed only whichever company or person
 * was currently selected in the form, so a row saved for one subject was simply
 * absent from a screen that had moved on to another. Decoupling them is what
 * makes a saved configuration reliably visible.</p>
 *
 * <p>The company picker is only available to a super admin, because choosing a
 * company is the whole of the authority being granted. A recruiter reaches the
 * same form scoped to their own company and can only spend it on colleagues:
 * the subject picker lists recruiters only, and the backend rejects a candidate
 * or a cross-company subject regardless of what arrives here.</p>
 */
const ConfigComponent = () => {
  const [context, setContext] = useState<ConfigurableContext | null>(null);
  const [companies, setCompanies] = useState<ConfigurableCompany[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [stats, setStats] = useState<DataServiceConfigStats | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [connections, setConnections] = useState<SearchableOption[]>([]);
  /** Kept in full because the form needs the console flag, not just the id. */
  const [allConnections, setAllConnections] = useState<DataConnection[]>([]);
  const [people, setPeople] = useState<SearchableOption[]>([]);
  const [connectionConfigs, setConnectionConfigs] = useState<DataServiceConfig[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Listing state, independent of the form.
  const [rows, setRows] = useState<DataServiceConfig[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [listScope, setListScope] = useState<'' | DataServiceConfigScope>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [revokingId, setRevokingId] = useState('');

  /**
   * Which half of Team Access is on screen. The two are the same records with the same
   * guards, so the listing simply asks for one source or the other; only the framing
   * differs - a manual grant is something an admin decided, a subscription grant is
   * something a verified payment decided.
   */
  const [accessTab, setAccessTab] = useState<DataServiceConfigSource>('MANUAL');

  // Form state.
  const [editingId, setEditingId] = useState('');
  const [scope, setScope] = useState<DataServiceConfigScope>('COMPANY');
  const [subjectId, setSubjectId] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [mode, setMode] = useState<TableAccessMode>('ALL');
  const [tables, setTables] = useState<string[]>([]);
  const [knownTables, setKnownTables] = useState<string[]>([]);
  const [tableDraft, setTableDraft] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  // Which screens this configuration grants. The builder defaults to on, the
  // console to off, matching the server so an untouched form round-trips.
  const [allowQueryBuilder, setAllowQueryBuilder] = useState(true);
  const [allowConsole, setAllowConsole] = useState(false);

  /**
   * The company this form applies to. A recruiter has exactly one and it is not
   * a choice, so it is pinned to the server-provided value; only a super admin
   * gets to name a different company, and that is the whole of the authority
   * being granted.
   */
  const effectiveCompanyId = context?.canManageCompany ? companyId : context?.companyId ?? '';

  /**
   * The connection the form is pointed at, kept in full because the console
   * toggle has to know whether the connection itself carries the capability.
   */
  const selectedConnection = useMemo(
    () => allConnections.find((c) => c.id === connectionId),
    [connectionId, allConnections]
  );

  const subjectLabel = useMemo(() => {
    const found = people.find((p) => p.value === subjectId);
    return found?.label ?? '';
  }, [subjectId, people]);

  const subjectOptions = useMemo<SearchableOption[]>(
    () =>
      (context?.subjects ?? [])
        .filter((subject) => subject.configurable)
        .map((subject) => ({
          value: subject.userId,
          label: subject.name ? `${subject.name} · ${subject.email}` : subject.email,
          hint: subject.role,
          keywords: `${subject.email} ${subject.name ?? ''} ${subject.role}`,
        })),
    [context]
  );

  const companyOptions = useMemo<SearchableOption[]>(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: company.companyName ?? company.id,
        hint: company.industry,
        keywords: `${company.companyName ?? ''} ${company.companyDomain ?? ''} ${company.industry ?? ''}`,
      })),
    [companies]
  );

  const loadStats = useCallback(async () => {
    try {
      const res = await getConfigStats();
      if (res.success && res.data) setStats(res.data);
    } catch {
      /* the counters are decorative; a failure must not hide the form */
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    try {
      const res = await getConfigurableCompanies();
      if (res.success && res.data) setCompanies(res.data);
    } catch {
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  const loadContext = useCallback(async () => {
    try {
      const res = await getConfigurableContext();
      if (res.success && res.data) {
        setContext(res.data);
        setPeople(
          res.data.subjects
            .filter((subject) => subject.configurable)
            .map((subject) => ({
              value: subject.userId,
              label: subject.name ? `${subject.name} · ${subject.email}` : subject.email,
              hint: subject.role,
              keywords: `${subject.email} ${subject.name ?? ''} ${subject.role}`,
            }))
        );
        if (res.data.canManageCompany) {
          void loadCompanies();
          // Default to the admin's own company so the picker is never a blank
          // that silently means "nobody" on submit.
          if (res.data.companyId) setCompanyId(res.data.companyId);
        }
      } else {
        setError(res.message ?? 'Could not work out what you are allowed to configure.');
      }
    } catch {
      setError('Could not work out what you are allowed to configure.');
    }
  }, [loadCompanies]);

  const loadConnections = useCallback(async () => {
    try {
      const res = await getDataConnections({}, 0, 100);
      if (res.success && res.data) {
        const list = res.data.page?.content ?? [];
        setAllConnections(list);
        setConnections(
          list.map((c) => ({
            value: c.id,
            label: c.alias,
            // The console flag is surfaced on the hint so an admin can see which
            // connections could take a console grant before picking one.
            hint: `${c.environment} · ${c.engine}${c.allowConsole ? ' · console on' : ''}`,
            keywords: `${c.alias} ${c.environment} ${c.engine}`,
          }))
        );
      }
    } catch {
      setError('Could not load connections.');
    }
  }, []);

  const loadTables = useCallback(async (id: string) => {
    if (!id) {
      setKnownTables([]);
      setConnectionConfigs([]);
      return;
    }
    try {
      const res = await getConnectionTables(id);
      setKnownTables(res.success && res.data ? (res.data.tables ?? []).map((t) => t.tableName) : []);
    } catch {
      setKnownTables([]);
    }
    try {
      // Who already holds a configuration on this connection. Listing it is
      // super-admin only, so a recruiter simply sees an empty list.
      const cfg = await getConnectionConfigs(id);
      setConnectionConfigs(cfg.success && cfg.data ? cfg.data : []);
    } catch {
      setConnectionConfigs([]);
    }
  }, []);

  /** The whole point of this: the listing never depends on the form. */
  const loadListing = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await getAllConfigs({
        search,
        scope: listScope || undefined,
        includeInactive,
        source: accessTab,
      });
      setRows(res.success && res.data ? res.data : []);
    } catch {
      setRows([]);
    } finally {
      setListLoading(false);
    }
  }, [search, listScope, includeInactive, accessTab]);

  useEffect(() => {
    void loadContext();
    void loadConnections();
  }, [loadContext, loadConnections]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    void loadListing();
  }, [loadListing]);

  useEffect(() => {
    void loadTables(connectionId);
  }, [connectionId, loadTables]);

  // Debounced so a typed search is one request rather than one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchDraft.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchDraft]);

  const resetForm = () => {
    setEditingId('');
    setSubjectId('');
    setConnectionId('');
    setMode('ALL');
    setTables([]);
    setTableDraft('');
    setExpiresAt('');
    setAllowQueryBuilder(true);
    setAllowConsole(false);
    setError('');
  };

  /** Loads a row into the form and switches it to update mode. */
  const beginEdit = (row: DataServiceConfig) => {
    if (!row.manageableByCaller) {
      setError('You can see this configuration but not change it.');
      return;
    }
    setEditingId(row.id);
    setScope(row.scope);
    setSubjectId(row.userId ?? '');
    // Without this the submit read the form's default company and could rewrite a
    // company-wide row onto whatever company was selected, or fail outright.
    setCompanyId(row.companyId ?? '');
    setConnectionId(row.connectionId);
    setMode(row.tableAccessMode);
    setTables(row.tables ?? []);
    setTableDraft('');
    setExpiresAt(toLocalInput(row.expiresAt));
    setAllowQueryBuilder(row.allowQueryBuilder !== false);
    setAllowConsole(Boolean(row.allowConsole));
    setError('');
    setNotice(`Editing the configuration for ${row.userEmail ?? row.companyName ?? 'this owner'}.`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!connectionId) {
      setError('Choose a connection.');
      return;
    }
    if (scope === 'COMPANY' && !effectiveCompanyId) {
      setError(context?.canManageCompany ? 'Choose a company.' : 'Your account is not linked to a company.');
      return;
    }
    if (scope === 'USER' && !subjectId) {
      setError('Choose who this configuration is for.');
      return;
    }
    if (mode === 'SELECTED' && tables.length === 0) {
      setError('Name the tables this configuration allows, or switch it to all tables.');
      return;
    }
    // The console is a connection-level capability first. Granting it on a
    // connection that does not have it would store a row that looks granted but
    // never takes effect, so say so before the save rather than after.
    if (allowConsole && !selectedConnection?.allowConsole) {
      setError(
        'The SQL console is switched off for this connection. A super admin has to enable it in the connection settings first.'
      );
      return;
    }

    const payload = {
      scope,
      companyId: scope === 'COMPANY' ? effectiveCompanyId : undefined,
      userId: scope === 'USER' ? subjectId : undefined,
      connectionId,
      tableAccessMode: mode,
      tables: mode === 'SELECTED' ? tables : undefined,
      allowQueryBuilder,
      allowConsole,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
    };

    setSaving(true);
    try {
      // An edit updates the row in place; a grant replaces any earlier one for
      // the same owner and connection.
      const res = editingId
        ? await updateDataServiceConfig(editingId, payload)
        : await saveDataServiceConfig(payload);

      if (res.success) {
        const who = scope === 'COMPANY' ? 'the company' : subjectLabel || 'the user';
        setNotice(
          res.message ??
            `${editingId ? 'Updated' : 'Configured'} ${res.data?.connectionAlias ?? 'the connection'} for ${who}.`
        );
        if (editingId) resetForm();
        await loadListing();
        await loadStats();
      } else {
        setError(res.message ?? res.errors ?? 'Could not save the configuration.');
      }
    } catch {
      setError('Could not save the configuration.');
    } finally {
      setSaving(false);
    }
  };

  const addTable = (value: string) => {
    const pattern = value.trim();
    if (!pattern) return;
    if (tables.includes(pattern)) {
      setTableDraft('');
      return;
    }
    setTables((prev) => [...prev, pattern]);
    setTableDraft('');
  };

  const revoke = async (row: DataServiceConfig) => {
    if (!window.confirm(
      `Revoke read access to "${row.connectionAlias ?? 'this connection'}" for ${
        row.userEmail ?? row.companyName ?? 'this owner'
      }? The record is kept, but access stops immediately.`
    )) {
      return;
    }
    setError('');
    setRevokingId(row.id);
    try {
      const res = await revokeDataServiceConfig(row.id);
      if (res.success) {
        setNotice(res.message ?? 'Configuration revoked.');
        if (editingId === row.id) resetForm();
        await loadListing();
        await loadStats();
      } else {
        setError(res.message ?? 'Could not revoke the configuration.');
      }
    } catch {
      setError('Could not revoke the configuration.');
    } finally {
      setRevokingId('');
    }
  };

  const scopeOptions: SearchableOption[] = CONFIG_SCOPE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
    hint: o.hint,
  }));

  const modeOptions: SearchableOption[] = TABLE_ACCESS_MODE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
    hint: o.hint,
  }));

  const tableOptions: SearchableOption[] = knownTables.map((t) => ({ value: t, label: t }));

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-6">
      <header className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 text-cyan-400" size={20} />
          <div>
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-100">
              Company &amp; user table configuration
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Grant a company or a single user read access to a connection, optionally narrowed to named
              tables. A company configuration is inherited by every recruiter in it; a candidate is granted
              individually. Everything granted here is read-only.
            </p>
          </div>
        </div>

        {stats && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              { label: 'Active', value: stats.totalActive },
              { label: 'Company-wide', value: stats.companyScoped },
              { label: 'Per user', value: stats.userScoped },
              { label: 'All tables', value: stats.allTables },
              { label: 'Table-restricted', value: stats.restrictedToTables },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{s.label}</p>
                <p className="mt-0.5 text-lg font-black text-cyan-300">{s.value}</p>
              </div>
            ))}
          </div>
        )}
      </header>

      {error && (
        <p className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {error}
        </p>
      )}
      {notice && !error && (
        <p className="flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
          <Check size={14} className="mt-0.5 shrink-0" />
          {notice}
        </p>
      )}

      <nav className="flex flex-wrap gap-2" aria-label="Team Access views">
        {ACCESS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setAccessTab(tab.value)}
            aria-current={accessTab === tab.value}
            className={`rounded-xl border px-4 py-2.5 text-left transition ${
              accessTab === tab.value
                ? 'border-cyan-500/50 bg-cyan-500/10'
                : 'border-slate-800 bg-slate-900/60 hover:border-slate-700'
            }`}
          >
            <span
              className={`block text-[11px] font-black uppercase tracking-widest ${
                accessTab === tab.value ? 'text-cyan-300' : 'text-slate-300'
              }`}
            >
              {tab.label}
            </span>
            <span className="mt-0.5 block text-[10px] text-slate-500">{tab.hint}</span>
          </button>
        ))}
      </nav>

      {accessTab === 'SUBSCRIPTION' && (
        <p className="flex items-start gap-2 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] px-3 py-2.5 text-[11px] leading-relaxed text-slate-400">
          <ShieldCheck size={13} className="mt-0.5 shrink-0 text-cyan-400" />
          These rows were created by a verified payment, not by hand. Each one carries the plan and the
          payment that produced it, and it covers the whole company. To change what a buyer gets, change the
          Data Service settings on the plan - a repeat purchase then replaces this row. Deleting it here stops
          access immediately, but the next payment for that plan grants it again.
        </p>
      )}

      {accessTab === 'MANUAL' && (
        <form onSubmit={submit} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-300">
            {editingId ? 'Edit configuration' : 'Grant or change access'}
          </h3>
          {editingId && (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-300">
              <Pencil size={11} /> Editing an existing row
            </span>
          )}
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {context?.canManageCompany ? (
            <label className="block space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Company</span>
              <SearchableSelect
                options={companyOptions}
                value={effectiveCompanyId}
                onChange={setCompanyId}
                loading={companiesLoading}
                placeholder="Search companies…"
                emptyLabel="Choose a company"
                searchable
              />
            </label>
          ) : (
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Company</p>
              <p className="mt-0.5 text-xs text-slate-200">{context?.companyName ?? 'Not linked to a company'}</p>
            </div>
          )}

          <label className="block space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Who</span>
            <SearchableSelect
              options={scopeOptions}
              value={scope}
              disabled={Boolean(editingId)}
              onChange={(value) => {
                setScope(value as DataServiceConfigScope);
                setSubjectId('');
              }}
              searchable={false}
            />
            {editingId && (
              <span className="block text-[10px] text-slate-500">
                The owner cannot be changed. Revoke this and create a new one instead.
              </span>
            )}
          </label>

          {scope === 'USER' && (
            <label className="block space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {context?.canManageCompany ? 'User' : 'Recruiter in your company'}
              </span>
              <SearchableSelect
                options={subjectOptions}
                value={subjectId}
                disabled={Boolean(editingId)}
                onChange={setSubjectId}
                placeholder="Search people…"
                emptyLabel={subjectOptions.length ? 'Anyone' : 'No one to configure'}
              />
              {subjectOptions.length === 0 ? (
                <span className="block text-[10px] text-amber-400/80">
                  There is nobody here you can configure. A super admin manages company-wide and candidate
                  access.
                </span>
              ) : (
                !context?.canManageCompany && (
                  <span className="block text-[10px] text-slate-500">
                    You can share with other recruiters in your company, up to your own access.
                  </span>
                )
              )}
            </label>
          )}

          <label className="block space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Connection</span>
            <SearchableSelect
              options={connections}
              value={connectionId}
              onChange={setConnectionId}
              placeholder="Search connections…"
              emptyLabel="No connection"
              searchable
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tables</span>
            <SearchableSelect
              options={modeOptions}
              value={mode}
              onChange={(value) => setMode(value as TableAccessMode)}
              searchable={false}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Expires (optional)
            </span>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/50"
            />
            <span className="block text-[10px] text-slate-500">
              Access stops at this moment. The row is kept so it can be reinstated.
            </span>
          </label>
        </div>

        {mode === 'SELECTED' && (
          <div className="mt-4 space-y-2 rounded-xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="flex items-center gap-2">
              <input
                value={tableDraft}
                onChange={(e) => setTableDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTable(tableDraft);
                  }
                }}
                placeholder="Add a table or pattern, e.g. app_user_*"
                className="flex-1 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/50"
              />
              <button
                type="button"
                onClick={() => addTable(tableDraft)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-cyan-500/40 px-3 py-2 text-xs font-bold text-cyan-300 hover:bg-cyan-500/10"
              >
                <Plus size={14} /> Add
              </button>
            </div>

            {knownTables.length > 0 && (
              <>
                <SearchableSelect
                  options={tableOptions}
                  value=""
                  onChange={addTable}
                  placeholder="Or pick from the connection…"
                  emptyLabel="Pick a table"
                  searchable
                />
                {connectionConfigs.length > 0 && (
                  <p className="text-[10px] text-slate-500">
                    {connectionConfigs.length} existing configuration
                    {connectionConfigs.length === 1 ? '' : 's'} on this connection, touching{' '}
                    {connectionConfigs
                      .flatMap((c) => (c.tableAccessMode === 'SELECTED' ? c.tables : ['*']))
                      .slice(0, 6)
                      .join(', ')}
                    {connectionConfigs.length > 6 ? '…' : ''}
                  </p>
                )}
              </>
            )}

            {tables.length === 0 ? (
              <p className="text-[10px] text-amber-400/80">
                No tables named yet, so this would save nothing until you add one.
              </p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {tables.map((t) => (
                  <li
                    key={t}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-200"
                  >
                    {t}
                    <button type="button" onClick={() => setTables((p) => p.filter((x) => x !== t))}>
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-slate-500">
              <code className="text-slate-400">*</code> matches any run of characters,{' '}
              <code className="text-slate-400">?</code> matches one. Omit a pattern to allow the table.
            </p>
          </div>
        )}

        <fieldset className="mt-5 rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
          <legend className="px-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
            What they can do
          </legend>
          <p className="text-[10px] text-slate-500">
            Table access above decides <em>which</em> tables are readable. These decide{' '}
            <em>which screens</em> they are read through.
          </p>

          <label className="mt-3 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={allowQueryBuilder}
              onChange={(e) => setAllowQueryBuilder(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-950 accent-cyan-500"
            />
            <span>
              <span className="block text-xs font-bold text-slate-200">Query Builder</span>
              <span className="block text-[10px] text-slate-500">
                The filter builder for browsing and exporting the allowed tables.
              </span>
            </span>
          </label>

          <label className="mt-3 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={allowConsole}
              onChange={(e) => setAllowConsole(e.target.checked)}
              disabled={!selectedConnection?.allowConsole}
              className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-950 accent-cyan-500 disabled:opacity-40"
            />
            <span>
              <span className="block text-xs font-bold text-slate-200">
                SQL Console
                <span className="ml-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-black uppercase text-amber-300">
                  raw SQL
                </span>
              </span>
              <span className="block text-[10px] text-slate-500">
                {selectedConnection
                  ? selectedConnection.allowConsole
                    ? 'Lets them run their own SQL against this connection, within the tables allowed above.'
                    : 'Switched off for this connection. A super admin has to enable it in the connection settings first.'
                  : 'Choose a connection to see whether it can take a console grant.'}
              </span>
            </span>
          </label>
        </fieldset>

        <div className="mt-4 flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : editingId ? <Pencil size={14} /> : <ShieldCheck size={14} />}
            {editingId ? 'Update configuration' : 'Save configuration'}
          </button>
          <button
            type="button"
            onClick={resetForm}
            className="rounded-xl border border-slate-800 px-3 py-2 text-xs font-bold text-slate-400 hover:text-slate-200"
          >
            {editingId ? 'Cancel edit' : 'Clear'}
          </button>
        </div>
      </form>
      )}

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-300">
            {accessTab === 'MANUAL' ? 'Manual configurations' : 'Automated grants'}
            <span className="ml-2 font-normal normal-case tracking-normal text-slate-500">
              {listLoading ? 'loading…' : `${rows.length} shown`}
            </span>
          </h3>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder="Search name, email, company, connection, table…"
                className="w-72 rounded-xl border border-slate-800 bg-slate-950 py-2 pl-9 pr-3 text-xs text-slate-200 outline-none focus:border-cyan-500/50"
              />
            </div>

            <SearchableSelect
              options={[
                { value: '', label: 'Any owner' },
                { value: 'COMPANY', label: 'Company-wide' },
                { value: 'USER', label: 'One user' },
              ]}
              value={listScope}
              onChange={(value) => setListScope(value as '' | DataServiceConfigScope)}
              searchable={false}
              className="w-44"
            />

            <label className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="accent-cyan-500"
              />
              Show expired
            </label>
          </div>
        </div>

        {listLoading && rows.length === 0 ? (
          <p className="mt-6 flex items-center gap-2 text-xs text-slate-400">
            <Loader2 size={14} className="animate-spin" /> Loading configurations…
          </p>
        ) : rows.length === 0 ? (
          <p className="mt-6 text-xs text-slate-500">
            {search || listScope
              ? 'Nothing matches that search.'
              : accessTab === 'MANUAL'
                ? 'Nothing configured yet. Use the form above to grant the first one.'
                : 'No payment has granted Data Service access yet. It appears here as soon as a payment for a Data Service plan is verified.'}
          </p>
        ) : accessTab === 'SUBSCRIPTION' ? (
          /**
           * Deliberately a different shape from the manual table. The questions an admin
           * asks are different too: not "who did I give this to" but "which company paid
           * for what, and is it still live" - so the plan and payment lead, and the row is
           * read-only because the plan is where the grant is defined.
           */
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 font-bold">Company</th>
                  <th className="px-3 py-2 font-bold">Who</th>
                  <th className="px-3 py-2 font-bold">Connection</th>
                  <th className="px-3 py-2 font-bold">Tables</th>
                  <th className="px-3 py-2 font-bold">What they can do</th>
                  <th className="px-3 py-2 font-bold">Granted by</th>
                  <th className="px-3 py-2 font-bold">Status</th>
                  <th className="px-3 py-2 text-right font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-b border-slate-800/60 text-xs ${row.active ? '' : 'opacity-60'}`}
                  >
                    <td className="px-3 py-2.5">
                      <p className="font-bold text-slate-100">{row.companyName ?? '—'}</p>
                      <p className="text-[10px] text-slate-500">via payment</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                        Whole company
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-slate-200">{row.connectionAlias ?? '—'}</p>
                      <p className="text-[10px] text-slate-500">{row.connectionEnvironment}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      {row.tableAccessMode === 'ALL' ? (
                        <span className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                          All
                        </span>
                      ) : (
                        <p className="flex max-w-[220px] flex-wrap gap-1">
                          {row.tables.slice(0, 3).map((t) => (
                            <span key={t} className="rounded bg-slate-800/70 px-1.5 py-0.5 text-[10px] text-slate-300">
                              {t}
                            </span>
                          ))}
                          {row.tables.length > 3 && (
                            <span className="text-[10px] text-slate-500">+{row.tables.length - 3}</span>
                          )}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="flex flex-wrap gap-1">
                        <span className="rounded-lg border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-300">
                          {row.permission === 'READ_WRITE' ? 'Read &amp; write' : 'Read only'}
                        </span>
                        {row.allowQueryBuilder !== false && (
                          <span className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                            Builder
                          </span>
                        )}
                        {row.allowConsole ? (
                          <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                            Console
                          </span>
                        ) : null}
                        {row.allowQueryBuilder === false && !row.allowConsole && (
                          <span className="text-[10px] text-slate-500">No surface</span>
                        )}
                      </p>
                      {row.allowConsole && !row.consoleAvailableOnConnection && (
                        <p className="text-[10px] text-amber-400/80">Console off on the connection</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[10px] text-slate-500">
                      <p className="font-bold text-slate-300">{row.planName ?? '—'}</p>
                      {row.paymentId && <p>payment {String(row.paymentId).slice(0, 8)}</p>}
                      {row.expiresAt && <p>expires {new Date(row.expiresAt).toLocaleDateString()}</p>}
                      {!row.expiresAt && <p>no expiry</p>}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.active ? (
                        <span className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
                          Active
                        </span>
                      ) : row.expired ? (
                        <span className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                          <TriangleAlert size={10} /> Expired
                        </span>
                      ) : (
                        <span className="rounded-lg border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Revoked
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => revoke(row)}
                          disabled={!row.manageableByCaller || !row.active}
                          title="Stop this access now. The next payment for the plan grants it again."
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 bg-slate-950 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-300 hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {revokingId === row.id ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <Trash2 size={11} />
                          )}
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 font-bold">Owner</th>
                  <th className="px-3 py-2 font-bold">Scope</th>
                  <th className="px-3 py-2 font-bold">Connection</th>
                  <th className="px-3 py-2 font-bold">Tables</th>
                  <th className="px-3 py-2 font-bold">Surfaces</th>
                  <th className="px-3 py-2 font-bold">Granted</th>
                  <th className="px-3 py-2 font-bold">Status</th>
                  <th className="px-3 py-2 text-right font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-b border-slate-800/60 text-xs ${
                      editingId === row.id ? 'bg-amber-500/5' : ''
                    } ${row.active ? '' : 'opacity-60'}`}
                  >
                    <td className="px-3 py-2.5">
                      <p className="font-bold text-slate-100">
                        {row.userName ?? row.companyName ?? '—'}
                      </p>
                      <p className="text-[10px] text-slate-500">{row.userEmail ?? row.companyName ?? row.userId}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="rounded-lg border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-300">
                        {row.scope === 'COMPANY' ? 'Company' : 'User'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-slate-200">{row.connectionAlias ?? '—'}</p>
                      <p className="text-[10px] text-slate-500">{row.connectionEnvironment}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      {row.tableAccessMode === 'ALL' ? (
                        <span className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                          All
                        </span>
                      ) : (
                        <p className="flex max-w-[220px] flex-wrap gap-1">
                          {row.tables.slice(0, 3).map((t) => (
                            <span key={t} className="rounded bg-slate-800/70 px-1.5 py-0.5 text-[10px] text-slate-300">
                              {t}
                            </span>
                          ))}
                          {row.tables.length > 3 && (
                            <span className="text-[10px] text-slate-500">+{row.tables.length - 3}</span>
                          )}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="flex flex-wrap gap-1">
                        {row.allowQueryBuilder !== false && (
                          <span className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                            Builder
                          </span>
                        )}
                        {row.allowConsole ? (
                          <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                            Console
                          </span>
                        ) : null}
                        {row.allowQueryBuilder === false && !row.allowConsole && (
                          <span className="text-[10px] text-slate-500">No surface</span>
                        )}
                      </p>
                      {/* A console grant on a connection without the capability
                          stored fine but does nothing, so it is called out. */}
                      {row.allowConsole && !row.consoleAvailableOnConnection && (
                        <p className="text-[10px] text-amber-400/80">
                          Console off on the connection
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[10px] text-slate-500">
                      <p>{row.grantedByName ?? '—'}</p>
                      {row.expiresAt && <p>expires {new Date(row.expiresAt).toLocaleDateString()}</p>}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.active ? (
                        <span className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
                          Active
                        </span>
                      ) : row.expired ? (
                        <span className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                          <TriangleAlert size={10} /> Expired
                        </span>
                      ) : (
                        <span className="rounded-lg border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Revoked
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => beginEdit(row)}
                          disabled={!row.manageableByCaller || !row.active}
                          title={
                            row.manageableByCaller
                              ? 'Edit'
                              : 'You can see this configuration but not change it.'
                          }
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-cyan-300 hover:border-cyan-500/40 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        <button
                          onClick={() => revoke(row)}
                          disabled={!row.manageableByCaller || !row.active}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 bg-slate-950 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-300 hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {revokingId === row.id ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <Trash2 size={11} />
                          )}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {context && !context.canManageCompany && (
          <p className="mt-4 flex items-start gap-2 text-[10px] text-slate-500">
            <Search size={12} className="mt-0.5 shrink-0" />
            You are seeing the recruiters you can configure. Company-wide grants are managed by an admin.
          </p>
        )}
      </div>
    </div>
  );
};

export default ConfigComponent;
